"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { toWeekKey, type WeekKey } from "../dates";
import { hrefWithSelectedWeek, readSelectedWeek, withSelectedWeek } from "../selected-week";
import { useCurrentWeekStart } from "./use-current-week";

export interface SelectedWeek {
  /** The explicitly chosen week, or null for "follow the current week". */
  selectedWeek: WeekKey | null;
  setSelectedWeek: (week: WeekKey | null) => void;
}

function readFromBrowser(): WeekKey | null {
  if (typeof window === "undefined") return null;
  return readSelectedWeek(window.location.search, window.history.state);
}

/** replaceState, only when something would actually change. */
function writeToBrowser(week: WeekKey | null): void {
  const href = hrefWithSelectedWeek(window.location.href, week);
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const state = withSelectedWeek(window.history.state, week);
  const stateWeek = readSelectedWeek("", window.history.state);
  if (href === current && stateWeek === week) return;
  window.history.replaceState(state, "", href);
}

/**
 * The one selected week, owned by App.tsx (R1-C step 4) and mirrored to
 * `?week=` and `history.state` (see ../selected-week.ts).
 *
 * It reads `window.location`, not `useSearchParams`: in Next 16.3 a native
 * replaceState does not update `useSearchParams`, and this hook is the writer.
 *
 * A selection REPLACES the current entry rather than pushing one. App.tsx
 * already pushes an entry per view change; pushing per week as well would make
 * Back step through weeks before it leaves a screen. View entries inherit the
 * week because App's URL-less pushState keeps the address and merges state.
 *
 * The write happens in the setter, not in an effect keyed on the state, so a
 * popstate can adopt an entry's week without rewriting the entry it landed on.
 */
export function useSelectedWeek(): SelectedWeek {
  const [selectedWeek, setWeek] = useState<WeekKey | null>(readFromBrowser);

  // Canonicalise what we were opened with: a mid-week date becomes its Sunday
  // and a malformed value leaves the address bar. Mount only.
  useEffect(() => {
    writeToBrowser(readFromBrowser());
  }, []);

  useEffect(() => {
    const onPopState = (event: PopStateEvent) => {
      setWeek(readSelectedWeek(window.location.search, event.state));
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const setSelectedWeek = useCallback((week: WeekKey | null) => {
    writeToBrowser(week);
    setWeek(week);
  }, []);

  // Re-apply `selectionForWeek`'s rule when the CLOCK is what moved. Every
  // write already stores the current week as null, but a week chosen on
  // Saturday becomes the current week at midnight, and nothing re-read it: the
  // tab stayed pinned to a WeekKey that now means "this week", so it would NOT
  // move on to the next week at the following boundary, and the URL kept a
  // param an ordinary session should never carry.
  //
  // This also collapses a shared link to the week that is already current,
  // which is the same rule arriving by a different door.
  const currentWeekStart = useCurrentWeekStart();
  useEffect(() => {
    if (selectedWeek !== null && selectedWeek === toWeekKey(currentWeekStart)) {
      setSelectedWeek(null);
    }
  }, [currentWeekStart, selectedWeek, setSelectedWeek]);

  return useMemo(() => ({ selectedWeek, setSelectedWeek }), [selectedWeek, setSelectedWeek]);
}

const SelectedWeekContext = createContext<SelectedWeek | null>(null);

/** App.tsx provides the value of its single `useSelectedWeek()` call. */
export const SelectedWeekProvider = SelectedWeekContext.Provider;

/**
 * For screens. Throws outside the provider on purpose: a silent default would
 * read as "the current week" and hide exactly the bug this contract prevents.
 * `useSelectedWeek` itself must be called once, by App.tsx; a second call would
 * be a second, independent copy of the state.
 */
export function useSelectedWeekContext(): SelectedWeek {
  const value = useContext(SelectedWeekContext);
  if (!value) throw new Error("useSelectedWeekContext must be used inside SelectedWeekProvider");
  return value;
}
